import React, { useState } from 'react';
import { getOrUndefined, map } from 'util/resource';

import { useUserProfiles } from 'apis/CompanyAPI/users/useUserProfiles';
import MessageField from 'domain/conversations/Message/MessageField';
import LoadableResource from 'util/resource/Resource';
import CompanyLogo from 'ui/elements/CompanyLogo';
import { ICompany } from 'types/company';
import Messages from './Message/Messages';
import { PostConversationMessage, Message as ApiMessage } from 'apis/CompanyAPI/conversations/conversationApi';
import { Message } from './Message/types';
import { PaginatedResult } from 'types/api';
import { useCompanyConversationMessages } from 'apis/CompanyAPI/conversations/useCompanyConversation';
import { useConversationActions } from 'apis/CompanyAPI/conversations/useConversationActions';
import ConversationHeader from './Message/ConversationHeader';
import cx from 'classnames';
import useRoleTrackingKeys from 'hooks/useRoleTrackingKeys';
import ConversationSettingsButton from './Message/ConversationSettingsButton';
import sortBy from 'ramda/src/sortBy';
import reverse from 'ramda/src/reverse';
import { useSelfUserProfile } from 'apis/CompanyAPI/users/useSelfUserProfile';
import useCompanyRole from 'hooks/useCompanyRole';
import InfoIcon from 'ui/elements/icons/InfoIcon';
import { getAdminRights } from 'auth/util';
import { Auth0User } from 'types';
import { useAuth0 } from '@auth0/auth0-react';
import { SendAsChoice } from './sendAs';

interface Props {
  company: ICompany;
  initialMessage?: string;
  onClose: () => void;
  /** Set when opened from the inbox, so the picker can hand you back to the entity's thread. */
  sendAs?: SendAsChoice;
}

/**
 * You cannot write to a company you administer as yourself — you would be talking to your own inbox.
 * Backoffice keeps the ability so support can reproduce what a member sees.
 */
export function useCanContactCompanyAsSelf(companySlug: string) {
  const { user: auth0User }: { user?: Auth0User } = useAuth0();
  const { canEditCompany } = useCompanyRole(companySlug);

  return !canEditCompany || getAdminRights(auth0User).includes('backoffice');
}

export default function CompanyConversationDialogAsUser({ company, initialMessage, onClose, sendAs }: Props) {
  const roleTrackingKeys = useRoleTrackingKeys(company.slug);
  const { resource: userProfileResource } = useSelfUserProfile();

  const { sendCompanyMessage } = useConversationActions();

  const [messagesResource, reloadMessages, seeMoreMessages] = useCompanyConversationMessages(company.id, 30);

  const messagesData = messagesResource.state === 'fetched' ? messagesResource.resource?.values || [] : [];
  const userProfiles = useUserProfiles(messagesData.map((m: Message) => m.creatorCwUserId));

  const [showSidebar, setShowSidebar] = useState(false);

  const title = <span className="u-wrap-text">{company.name}</span>;
  const logo = <CompanyLogo company={company} size={40} />;

  const onReply = async (message: PostConversationMessage) => {
    return await sendCompanyMessage({ companyId: company.id, message });
  };

  const withProfile = getOrUndefined(userProfileResource);

  const canContactCompany = useCanContactCompanyAsSelf(company.slug);

  const messages = map(messagesResource, (r: PaginatedResult<ApiMessage>) => ({
    ...r,
    values: sortBy(
      (a: Message) => new Date(a.createdAt),
      reverse(r.values).map(
        (c: ApiMessage): Message => ({
          ...c,
          align: withProfile?.cwUserId === c.creatorCwUserId ? ('right' as const) : ('left' as const),
        }),
      ),
    ),
  }));

  return (
    <LoadableResource resource={userProfileResource}>
      {userProfile => (
        <Messages
          addMessage={() => reloadMessages()}
          user={userProfile}
          userProfiles={userProfiles}
          onClose={onClose}
          showSidebar={showSidebar}
          setShowSidebar={(v: boolean) => setShowSidebar(v)}
          header={
            <ConversationHeader
              title={title}
              logo={logo}
              suffix={
                <ConversationSettingsButton
                  onClick={() => setShowSidebar(!showSidebar)}
                  text={showSidebar ? 'Close Settings' : 'Show Settings'}
                /> //header
              }
            />
          }
          conversation={messages}
          seeMore={seeMoreMessages}
          senderCompanies={[company]}
        >
          {canContactCompany ? (
            <MessageField
              initialMessage={initialMessage}
              sendAs={sendAs}
              placeholder="Write a message"
              handleSubmit={onReply}
              onSuccess={() => reloadMessages()}
              submitClassName={cx(
                'data-track-conversation-send-message',
                'data-track-conversation-send-message-as-company-user',
                roleTrackingKeys,
              )}
              padding="none"
              autoFocus
            />
          ) : (
            <p className="u-align-center u-content-padding">
              <InfoIcon className="u-half-spacing-right"></InfoIcon>You&apos;re attempting to message a company you are
              an admin of.
            </p>
          )}
        </Messages>
      )}
    </LoadableResource>
  );
}
